import React, {useState, useEffect, useMemo} from "react";
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';
import classNames from "classnames";
import moment from 'moment';
import PropTypes from 'prop-types';
import * as d3 from 'd3';
import _ from 'lodash';
import ReactTooltip from "react-tooltip";

function Timebar({
  // width = '100%',
  height = 20,
  lower = '2000-01-01T00:00:00Z',
  upper = '2020-12-31T23:59:59Z',
  start = undefined,
  end = undefined,
  margin = 8,
  fill = '#ffcc33',
  drawAxis = true,
  segments = [],
}) {

  const [width, setWidth] = useState(300);
  // const [x, setX] = useState(0);
  // const [x2, setX2] = useState(0);
  // const [w, setW] = useState(0);
  const [tipId, setTipId] = useState(_.uniqueId('timebar'));
  // const [years, setYears] = useState([2004, 2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014, 2015, 2016, 2017, 2018, 2019, 2020]);

  // translate the dates into a scale
  let lowerM = moment.utc(lower),
    upperM = moment.utc(upper),
    startM = start ? moment.utc(start) : lowerM.clone(),
    endM = end ? moment.utc(end) : upperM.clone(),
    isDone = Math.abs(moment.utc().diff(endM, 'days')) > 60,
    startFmt = startM.format('YYYY-MM-DD'),
    endFmt = (isDone) ? endM.format('YYYY-MM-DD') : 'ongoing';

  const xScale = useMemo(() => {
    return d3.scaleLinear(
      [lowerM.unix(), upperM.unix()],
      [margin, width - 2 * margin]
    )
  }, [lowerM, upperM, margin, width]);

  const yScale = useMemo(() => {
    let dataMin = _.minBy(segments, 'count'),
      dataMax = _.maxBy(segments, 'count');

    return d3.scaleLinear(
      [dataMin ? dataMin.count : 0, dataMax ? dataMax.count : 100],
      [2, 10]
    )
  }, [segments])

  const dataSegments = useMemo(() => {
    return segments.map(seg => {
      let segDate = moment.utc(seg.month),
        segEnd = segDate.clone().endOf('M');

      return {
        startUnix: segDate.unix(),
        endUnix: segEnd.unix(),
        monthFmt: segDate.format("MMM YYYY"),
        count: seg.count
      }
    })
  }, [segments]);

  // useEffect(() => {
  //   let localX = xScale(startM.unix()),
  //     localX2 = xScale(endM.unix()),
  //     localW = localX2 - localX;

  //   setX(localX);
  //   setX2(localX2);
  //   setW(localW);
  // }); // should just be on width, but if you do that, resorting the catalog doesn't update this component?

  const onAutoResize = (e) => {
    let newWidth = e.width - 8 * 2;    // @XXX FUDGE: 8 pixel padding in tds in bootstrap
    setWidth(newWidth);
  }

  return (
    <div className="timebar">
      <ReactTooltip id={tipId} place="left" effect="solid">
        {/* {startM.toISOString()} */}
      </ReactTooltip>

      <AutoSizer disableHeight onResize={onAutoResize}>
        {({ width }) => (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={width}
            height={height}
          >
            {/* {drawAxis && (
              <g transform={`translate(0, ${height - 4})`}>
                <line
                  x1={0}
                  x2={width}
                  y1={0}
                  y2={0}
                  stroke="#ff0000"
                />

                {years.map(y => {
                  return (
                    <g
                      key={y}
                      transform={`translate(${xScale(moment().year(y).unix())}, 0)`}
                    >
                      <line 
                        y2={4}
                        stroke="#ff0000"
                        strokeWidth={2}
                      />
                    </g>
                  )
                })}
              </g>
            )} */}

            {segments.length && (
              <>
                {dataSegments.map(seg => {
                  return (
                    // <rect
                    //   key={seg.startUnix}
                    //   x={xScale(seg.startUnix)}
                    //   y={6}
                    //   width={xScale(seg.endUnix) - xScale(seg.startUnix)}
                    //   height="8"
                    //   fill={fill}
                    //   rx="2"
                    // />

                    <line
                      key={seg.startUnix}
                      x1={xScale(seg.startUnix)}
                      x2={xScale(seg.endUnix)}
                      y1={7}
                      y2={7}
                      stroke={fill}
                      strokeWidth={yScale(seg.count)}
                      data-tip={`${seg.monthFmt}: ${seg.count} obs`}
                      data-for={tipId}
                    />
                  )
                })}
              </>
            )}

            {!segments.length && (
              <g
                transform={`translate(0, 0)`}
                data-tip={`${startFmt} - ${endFmt}`}
                data-for={tipId}
              >
                <rect
                  x={xScale(startM.unix())}
                  y="6"
                  width={xScale(endM.unix()) - xScale(startM.unix())}
                  height="8"
                  fill={fill}
                  rx="2"
                />

                <circle cx={xScale(startM.unix())} cy="10" r="8" fill={fill}>
                  <title>{startM.toISOString()}</title>
                </circle>

                {isDone && (
                  <circle cx={xScale(endM.unix())} cy="10" r="8" fill={fill}>
                    <title>{endM.toISOString()}</title>
                  </circle>
                )}

                {!isDone && (
                  <g transform={`translate(${xScale(endM.unix()) - 4}, 0)`}>
                    <polygon points={`0 0, 14 10, 0 20`} fill={fill} />
                  </g>
                )}
              </g>
            )}
          </svg>
        )}
      </AutoSizer>
    </div>
  );
}

Timebar.propTypes = {
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  lower: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(moment)
  ]),
  upper: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(moment)
  ]),
  start: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(moment)
  ]),
  end: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(moment)
  ]),
  margin: PropTypes.number,
  fill: PropTypes.string,
}

export default Timebar;